import { useState } from 'react'
import { Link } from 'react-router-dom'
import { QrCode, BarChart3, Users, ArrowRight, CheckCircle } from 'lucide-react'
import LeadCaptureForm from '../components/LeadCaptureForm'

export default function Landing() {
  const [showPricing, setShowPricing] = useState(false)
  const [capturedLead, setCapturedLead] = useState<{name: string, email: string, phone: string} | null>(null)

  const handleLeadCaptured = (lead: {name: string, email: string, phone: string}) => {
    setCapturedLead(lead)
    setShowPricing(true)
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900">InsideThisHome</h1>
          <div className="flex gap-4">
            <Link to="/login" className="px-4 py-2 text-gray-600 hover:text-gray-900">
              Sign in
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-5xl font-bold text-gray-900 mb-6">
              QR Codes That Sell Homes
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Turn every yard sign into a lead-generating machine. 
              Dynamic listing pages, instant notifications, and powerful analytics 
              for modern real estate teams.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <a 
                href="https://calendly.com/realestateos/inside-this-home-onboarding"
                target="_blank"
                rel="noopener noreferrer"
                className="px-8 py-3 border-2 border-gray-200 rounded-lg hover:border-primary"
              >
                Book Demo
              </a>
            </div>
          </div>

          {/* Lead Capture or Pricing */}
          <div>
            {!showPricing ? (
              <LeadCaptureForm onCaptured={handleLeadCaptured} />
            ) : (
              <PricingStep lead={capturedLead} />
            )}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <QrCode className="text-primary" size={24} />
              </div>
              <h3 className="text-lg font-semibold mb-2">Dynamic QR Codes</h3>
              <p className="text-gray-600">
                One QR code per sign. Update listings anytime without reprinting.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <Users className="text-primary" size={24} />
              </div>
              <h3 className="text-lg font-semibold mb-2">Instant Leads</h3>
              <p className="text-gray-600">
                Get notified the moment someone scans your sign. 
                Capture buyer info before they drive away.
              </p>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-sm">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <BarChart3 className="text-primary" size={24} />
              </div>
              <h3 className="text-lg font-semibold mb-2">Powerful Analytics</h3>
              <p className="text-gray-600">
                Track scans, conversion rates, and ROI per sign. 
                Know which properties are getting the most attention.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-12">
        <div className="max-w-6xl mx-auto px-6 text-center text-gray-500">
          <p>&copy; 2026 InsideThisHome. A Real Estate OS product.</p>
        </div>
      </footer>
    </div>
  )
}

function PricingStep({ lead }: { lead: {name: string, email: string, phone: string} | null }) {
  const [plan, setPlan] = useState<'monthly' | 'annual'>('monthly')
  const [loading, setLoading] = useState(false)

  const handleContinue = async () => {
    if (!lead) return
    
    setLoading(true)
    
    // Store lead info and redirect to full signup with plan pre-selected
    localStorage.setItem('ith_lead_name', lead.name)
    localStorage.setItem('ith_lead_email', lead.email)
    localStorage.setItem('ith_lead_phone', lead.phone)
    localStorage.setItem('ith_selected_plan', plan)
    
    // Redirect to signup with pre-filled data
    window.location.href = `/signup?plan=${plan}&email=${encodeURIComponent(lead.email)}`
  }

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="flex items-center gap-2 mb-6 text-green-600">
        <CheckCircle size={20} />
        <span className="text-sm font-medium">Info captured! Choose your plan:</span>
      </div>

      <h3 className="text-xl font-bold mb-4">Select Your Plan</h3>
      
      <div className="space-y-3 mb-6">
        <button
          onClick={() => setPlan('monthly')}
          className={`w-full p-4 border-2 rounded-lg text-left ${
            plan === 'monthly' ? 'border-primary bg-primary/5' : 'border-gray-200'
          }`}
        >
          <div className="flex justify-between items-center">
            <div>
              <div className="font-semibold">Monthly</div>
              <div className="text-sm text-gray-500">10 QR codes included</div>
            </div>
            <div className="text-2xl font-bold">$49<span className="text-sm font-normal">/mo</span></div>
          </div>
        </button>

        <button
          onClick={() => setPlan('annual')}
          className={`w-full p-4 border-2 rounded-lg text-left ${
            plan === 'annual' ? 'border-primary bg-primary/5' : 'border-gray-200'
          }`}
        >
          <div className="flex justify-between items-center">
            <div>
              <div className="font-semibold">Annual</div>
              <div className="text-sm text-green-600">Save 2 months</div>
            </div>
            <div className="text-2xl font-bold">$490<span className="text-sm font-normal">/yr</span></div>
          </div>
        </button>
      </div>

      <button
        onClick={handleContinue}
        disabled={loading}
        className="w-full py-3 bg-primary text-white rounded-lg hover:bg-primary-dark flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? 'Loading...' : 'Continue to Payment'}
        <ArrowRight size={18} />
      </button>

      <p className="text-xs text-gray-500 text-center mt-4">
        Need more codes? Add 10 for $25/mo after signup
      </p>
    </div>
  )
}
